import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatMenu } from '@angular/material/menu';
import {
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterType,
  AudienceFilterOperator,
  AudienceFilterTimeOperator,
  AudienceFilterTimeValue,
  AudienceFilterFrequencyOperator,
  AudienceFilterSalesOperator,
  WeatherTypeModel,
  WeatherPropertiesModel,
  TimeFrame,
  AudienceFilterInputType,
} from '../../../audience/components/audience.model';
import { IntegrationType } from '../../../automations/integrations-models';
import {
  SessionCondition,
  SessionConditionType,
  SessionConditionCampaignEventConfig,
} from '../../../audience/components/session-condition-group.model';
import AudienceHelpers from '../../../audience/audience-helpers';
import SessionConditionHelpers from '../../../audience/session-condition-helpers';
import {
  applyComparison,
  comparisonOptionsFor,
  isValuelessType,
  minimumValueFor,
  SessionConditionComparison,
  SessionConditionComparisonOption,
  toComparison,
} from '../../../audience/session-condition-comparison';
import { SelectionsService } from '../../../shared/services/selections.service';
import { IntegrationsService } from '../../../shared/services/integrations.service';
import { LoadSuggestionsService } from '../../../search/load-suggestions.service';
import { TranslateService } from '@ngx-translate/core';
import { WidgetSimpleInfo } from '../../../campaign/widgets.model';
import { inclusiveEndDate } from '../../../data-platform/audiences/inclusive-end-date.utils';

@Component({
  selector: 'app-audience-inline-editor',
  templateUrl: './audience-inline-editor.component.html',
  styleUrls: ['./audience-inline-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudienceInlineEditorComponent implements OnInit, OnChanges {
  @Input() filter?: AudienceFilter;
  @Input() condition?: SessionCondition;
  @Input() allWidgets: readonly WidgetSimpleInfo[] = [];
  @Input() accountDomain?: string;
  @Input() readOnly = false;

  @Output() filterChange = new EventEmitter<AudienceFilter>();
  @Output() conditionChange = new EventEmitter<SessionCondition>();
  @Output() remove = new EventEmitter<void>();

  /**
   * When provided, the criterion's type label becomes a button that opens THIS
   * shared picker (the parent's unified "Add criterion" panel) so changing a
   * condition/filter type uses the same searchable picker as adding. The editor
   * emits `changeTypeRequested` first so the parent targets this row for
   * replacement. Absent → the type stays a static label (legacy behaviour).
   */
  @Input() sharedTypeMenu?: MatMenu;
  @Output() changeTypeRequested = new EventEmitter<void>();
  /** Fires when the shared picker opens from this row's type chip, so the parent
   *  can move focus into the search box. */
  @Output() typeMenuOpened = new EventEmitter<void>();

  protected form!: FormGroup;
  private storedEndDate?: string;
  protected loadingOptions = false;
  protected choosableValueOptions: Array<{ key: any; value: any }> = [];
  protected choosableValueTagOptions: Array<{ key: any; value: any }> = [];

  protected showOperator = false;
  protected supportedOperators: AudienceFilterOperator[] = [];
  protected comparisonOptions: SessionConditionComparisonOption[] = [];
  protected operatorFirst = false;
  protected minimumValue?: number;
  protected filterInputType?: AudienceFilterInputType;
  protected isCampaignCondition = false;
  protected isCountryFilter = false;

  // Enums for Template usage
  protected readonly InputTypes = AudienceFilterInputType;
  protected readonly FilterTypes = AudienceFilterType;
  protected readonly ConditionTypes = SessionConditionType;
  protected readonly SalesOperators = [
    AudienceFilterSalesOperator.Exactly,
    AudienceFilterSalesOperator.LessThan,
    AudienceFilterSalesOperator.MoreThan,
    AudienceFilterSalesOperator.Between,
    AudienceFilterSalesOperator.Any,
  ];
  protected readonly FreqOperators = [AudienceFilterFrequencyOperator.Exactly, AudienceFilterFrequencyOperator.AtLeast];
  protected readonly WeatherTypeOptions = [
    WeatherTypeModel.Sunny,
    WeatherTypeModel.Cloudy,
    WeatherTypeModel.Rainy,
    WeatherTypeModel.Snowy,
    WeatherTypeModel.Temperature,
  ];
  protected readonly Timeframes = [
    AudienceFilterTimeOperator.AnyTime,
    AudienceFilterTimeOperator.TimeWindow,
    AudienceFilterTimeOperator.SinceDate,
    AudienceFilterTimeOperator.BetweenDates,
    AudienceFilterTimeOperator.BetweenDaysAgo,
  ];
  protected readonly CampaignOperators = [
    AudienceFilterOperator.Equals,
    AudienceFilterOperator.MoreThan,
    AudienceFilterOperator.LessThan,
  ];

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly selectionsService = inject(SelectionsService);
  private readonly integrationsService = inject(IntegrationsService);
  private readonly loadSuggestionsService = inject(LoadSuggestionsService);
  private readonly translateService = inject(TranslateService);

  ngOnInit(): void {
    this.initEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const filterChange = changes['filter'];
    if (filterChange && !filterChange.firstChange) {
      const prev: AudienceFilter | undefined = filterChange.previousValue;
      const curr: AudienceFilter | undefined = filterChange.currentValue;
      if (prev?.type !== curr?.type || prev?.app !== curr?.app) {
        this.initEditor();
        return;
      }
      // Value/operator changed — update input type without refetching options.
      if (curr && this.form) {
        this.filterInputType = AudienceHelpers.getInputTypeForFilterAndOperator(curr.type!, curr.operator, curr.app);
      }
      return;
    }

    if (changes['condition'] && !changes['condition'].firstChange) {
      const prev = changes['condition'].previousValue as SessionCondition | undefined;
      const curr = changes['condition'].currentValue as SessionCondition | undefined;
      // Only rebuild when the condition TYPE changes. A value/operator echo from
      // the parent must NOT rebuild the form — doing so resets the live control
      // (e.g. the campaign picker cleared itself right after selecting).
      if (prev?.type !== curr?.type) {
        this.initEditor();
        return;
      }
      if (curr && this.form) {
        this.filterInputType = SessionConditionHelpers.getInputTypeForCondition(curr);
      }
      return;
    }

    if (changes['allWidgets'] && !changes['allWidgets'].firstChange) {
      this.initEditor();
    }
  }

  private initEditor(): void {
    this.isCampaignCondition = this.checkIsCampaignCondition();

    if (this.filter) {
      const type = this.filter.type || AudienceFilterType.Identified;
      const config =
        this.filter.filterConfig || AudienceHelpers.getFilterConfig(type, this.filter.app, this.filter.frequencyValue);
      this.filter.filterConfig = config;

      this.showOperator = this.shouldShowOperator(type);
      this.supportedOperators = config?.supportedOperators || [];
      this.filterInputType = AudienceHelpers.getInputTypeForFilterAndOperator(
        type,
        this.filter.operator,
        this.filter.app,
      );

      this.buildFilterForm();
      this.loadChoosableOptionsForFilter();
    } else if (this.condition) {
      const type = this.condition.type || SessionConditionType.NewVisitor;
      const config = this.condition.conditionConfig || SessionConditionHelpers.getSessionConditionConfig(type);
      this.condition.conditionConfig = config;

      this.comparisonOptions = comparisonOptionsFor(type);
      this.operatorFirst = isValuelessType(type);
      this.showOperator = this.comparisonOptions.length >= 2 || config?.supportedOperators?.length >= 2;
      this.supportedOperators = config?.supportedOperators || [];
      this.filterInputType = SessionConditionHelpers.getInputTypeForCondition(this.condition);

      this.buildConditionForm();
      this.loadChoosableOptionsForCondition();
    }

    this.subscribeToFormChanges();
  }

  private checkIsCampaignCondition(): boolean {
    if (!this.condition) return false;
    const t = this.condition.type;
    return (
      t === SessionConditionType.CampaignOpened ||
      t === SessionConditionType.CampaignClickthrough ||
      t === SessionConditionType.CampaignConversion ||
      t === SessionConditionType.CampaignDismissal ||
      t === SessionConditionType.CampaignSurveyResponse
    );
  }

  private shouldShowOperator(type: AudienceFilterType): boolean {
    return (
      type !== AudienceFilterType.Identified &&
      type !== AudienceFilterType.Weather &&
      type !== AudienceFilterType.Temperature
    );
  }

  // ── Form Builders ─────────────────────────────────────────────────────────

  private buildFilterForm(): void {
    let val = this.filter!.value;
    let arrayVal: string[] = [];
    if (this.filterInputType === AudienceFilterInputType.MultiChoiceDropdown) {
      arrayVal = val ? val.replace(/ /g, '').split(',') : [];
    }

    // Timeframe parsing
    let timeMode = this.filter!.timeOperator || AudienceFilterTimeOperator.AnyTime;
    let days: number | null = null;
    let daysUpper: number | null = null;
    let start: Date | null = null;
    let end: Date | null = null;
    this.storedEndDate = undefined;

    if (this.filter!.timeValue) {
      try {
        const parsed: AudienceFilterTimeValue =
          typeof this.filter!.timeValue === 'string' ? JSON.parse(this.filter!.timeValue) : this.filter!.timeValue;
        days = parsed.days ?? null;
        daysUpper = parsed.upperDays ?? null;
        start = parsed.startDate ? new Date(parsed.startDate) : null;
        end = parsed.endDate ? new Date(parsed.endDate) : null;
        this.storedEndDate = parsed.endDate instanceof Date ? parsed.endDate.toISOString() : parsed.endDate;
      } catch {}
    }

    // Purchase properties
    const props = this.filter!.purchaseProperties;
    const salesOp = props?.operator || AudienceFilterSalesOperator.Any;
    const salesVal = props?.value ?? 0;
    const salesUp = props?.upperValue ?? 0;
    const coupon = props?.appliedCoupon ?? false;

    // Weather properties
    const wp = this.filter!.weatherProperties || ({} as WeatherPropertiesModel);
    const weatherType = wp.weatherType || WeatherTypeModel.Temperature;
    const weatherTimeframe = wp.specificDay
      ? TimeFrame.SpecificDay
      : wp.daysForward
        ? TimeFrame.DaysForward
        : wp.checkNextWeekend
          ? TimeFrame.CheckNextWeekend
          : wp.checkToday
            ? TimeFrame.CheckToday
            : wp.checkTomorrow
              ? TimeFrame.CheckTomorrow
              : wp.inDays
                ? TimeFrame.InDays
                : TimeFrame.Now;

    this.form = this.fb.group({
      operator: [this.filter!.operator],
      value: [val],
      valueArray: [arrayVal],
      // Goals / Purchase frequency
      frequencyType: [this.filter!.frequencyType || this.filter!.filterConfig?.defaultFrequencyType || 'AtLeast'],
      frequencyValue: [
        this.filter!.frequencyValue || this.filter!.filterConfig?.defaultFrequencyValue || 1,
        [Validators.min(0)],
      ],
      // Timeframe
      timeOperator: [timeMode],
      days: [days ?? 30],
      daysUpper: [daysUpper ?? 90],
      startDate: [start],
      endDate: [end],
      // Sales details
      salesOperator: [salesOp],
      salesValue: [salesVal, [Validators.min(0)]],
      salesUpperValue: [salesUp, [Validators.min(0)]],
      appliedCoupon: [coupon],
      // Weather details
      weatherType: [weatherType],
      longitude: [wp.customLongitude],
      latitude: [wp.customLatitude],
      minTemp: [wp.minTemperature],
      maxTemp: [wp.maxTemperature],
      minWind: [wp.minWindspeed],
      maxWind: [wp.maxWindspeed],
      weatherTimeframe: [weatherTimeframe],
      weatherDaysForward: [wp.daysForward ?? 1],
      weatherSpecificDay: [wp.specificDay ? new Date(wp.specificDay) : new Date()],
      weatherInDays: [wp.inDays ?? 1],
      // App tags sub dropdown selection
      filterValueTagString: [this.filter!.filterTranslations?.value ?? ''],
    });
  }

  private buildConditionForm(): void {
    const val = this.condition!.value;
    let arrayVal: string[] = [];
    if (this.filterInputType === AudienceFilterInputType.MultiChoiceDropdown) {
      arrayVal = val ? val.replace(/ /g, '').split(',') : [];
    }

    let campaignEvOp = AudienceFilterOperator.Equals;
    let campaignEvCount = 1;
    // The campaign picker (app-widget-selector-menu) keys on numeric ids, so the
    // campaign condition's `value` control holds the campaignId as a number.
    let campaignValue: number | null = null;

    if (this.isCampaignCondition && val) {
      try {
        if (isNaN(+val)) {
          const parsed = JSON.parse(val) as SessionConditionCampaignEventConfig;
          campaignValue = parsed.campaignId ?? null;
          campaignEvCount = parsed.times ?? 1;
        } else {
          campaignValue = +val;
          campaignEvCount = 1;
        }
        campaignEvOp = this.condition!.operator || AudienceFilterOperator.Equals;
      } catch {}
    }

    const comparison = toComparison(this.condition!);
    this.minimumValue = minimumValueFor(comparison);
    this.form = this.fb.group({
      operator: [this.condition!.operator],
      comparison: [comparison],
      value: [this.isCampaignCondition ? campaignValue : val, this.minimumValidators()],
      valueArray: [arrayVal],
      // Campaign Condition Specifics
      campaignEventOperator: [campaignEvOp],
      campaignEventCount: [campaignEvCount, [Validators.min(0), Validators.max(10)]],
    });
    this.form.get('value')!.markAsTouched();
  }

  private minimumValidators(): ValidatorFn[] {
    return this.minimumValue === undefined ? [] : [Validators.required, Validators.min(this.minimumValue)];
  }

  private onComparisonChanged(comparison: SessionConditionComparison): void {
    this.minimumValue = minimumValueFor(comparison);
    const value = this.form.get('value')!;
    value.setValidators(this.minimumValidators());
    value.updateValueAndValidity({ emitEvent: false });
    this.filterInputType = SessionConditionHelpers.getInputTypeForCondition(
      applyComparison(this.condition!, comparison),
    );
  }

  // ── Options Loaders ───────────────────────────────────────────────────────

  private loadChoosableOptionsForFilter(): void {
    const type = this.filter!.type;
    this.isCountryFilter = false;

    if (type === AudienceFilterType.Consent) {
      this.loadingOptions = true;
      this.selectionsService.getVersions().then((versions) => {
        this.choosableValueOptions = (versions || []).map((v) => ({ key: v.id, value: v.displayName }));
        this.loadingOptions = false;
      });
    } else if (type === AudienceFilterType.Goals || type === AudienceFilterType.Purchase) {
      this.loadingOptions = true;
      this.selectionsService.getGoals().subscribe({
        next: (goals) => {
          this.choosableValueOptions = (goals || [])
            .map((g) => ({
              key: g.id,
              value: g.displayName,
            }))
            .sort((a, b) => a.value.localeCompare(b.value));
          this.loadingOptions = false;
        },
        error: () => (this.loadingOptions = false),
      });
    } else if (type === AudienceFilterType.Tags && !this.isAppTags) {
      this.loadingOptions = true;
      this.selectionsService.getLabelsAsync().then((labels) => {
        this.choosableValueOptions = (labels || []).map((l) => ({ key: l.id, value: l.displayName }));
        this.loadingOptions = false;
      });
    } else if (type === AudienceFilterType.InterestProfiles) {
      this.loadingOptions = true;
      this.selectionsService.getInterestProfilesAsync().then((profiles) => {
        this.choosableValueOptions = (profiles || []).map((p) => ({ key: p.id, value: p.displayName }));
        this.loadingOptions = false;
      });
    } else if (type === AudienceFilterType.Country) {
      this.isCountryFilter = true;
    } else if (
      type === AudienceFilterType.AppList ||
      type === AudienceFilterType.AppSegment ||
      type === AudienceFilterType.AppLabels ||
      this.isAppTags
    ) {
      if (this.filter!.filterConfig?.skipValueSelection) {
        const appName = this.integrationsService.getAppName(this.filter!.app);
        const integration = this.integrationsService.activeIntegrationsBS.value?.find((i) => i.type === appName);
        if (integration?.data?.group) {
          this.choosableValueOptions = [{ key: integration.data.group, value: integration.data.group }];
          this.integrationsService.getSettings(this.filter!).subscribe((res) => {
            const match = res?.find((item) => item.id === integration.data.group);
            if (match) {
              this.choosableValueOptions = [{ key: match.id, value: match.name }];
            }
          });
        }
      } else {
        this.loadingOptions = true;
        this.integrationsService.getSettings(this.filter!).subscribe({
          next: (res) => {
            this.choosableValueOptions = (res || []).map((item) => ({ key: item.id, value: item.name }));
            this.loadingOptions = false;
            if (this.isAppTags && this.form.get('value')?.value) {
              this.loadAppTagsSubOptions(this.form.get('value')?.value);
            }
          },
          error: () => (this.loadingOptions = false),
        });
      }
    }
  }

  private loadChoosableOptionsForCondition(): void {
    const type = this.condition!.type;

    if (type === SessionConditionType.UTMCampaign) {
      this.loadingOptions = true;
      this.loadSuggestionsService.getCampaignsAsync().then((camps) => {
        this.choosableValueOptions = (camps || []).map((c) => ({ key: c.id, value: c.displayName }));
        this.loadingOptions = false;
      });
    } else if (type === SessionConditionType.BrowserLanguage) {
      this.choosableValueOptions = SessionConditionHelpers.getBrowserLanguages();
    } else if (type === SessionConditionType.OperatingSystem) {
      this.choosableValueOptions = SessionConditionHelpers.getOperatingSystems();
    } else if (this.isCampaignCondition) {
      this.choosableValueOptions = SessionConditionHelpers.widgetsToCampaignsAsChoosableOptions(
        this.allWidgets as WidgetSimpleInfo[],
        this.translateService,
      );
    } else if (type === SessionConditionType.IdentifiedEmailMarketing) {
      this.loadingOptions = true;
      this.integrationsService.getQueryStrings().subscribe({
        next: (res) => {
          this.choosableValueOptions = Object.keys(res || {}).map((x) => ({ key: x, value: x }));
          this.loadingOptions = false;
        },
        error: () => (this.loadingOptions = false),
      });
    }
  }

  protected onFilterValueSelected(val: any): void {
    if (this.isAppTags) {
      this.loadAppTagsSubOptions(val);
    }
  }

  private loadAppTagsSubOptions(listId: string): void {
    this.loadingOptions = true;
    this.integrationsService.getTags(this.filter!, listId).subscribe({
      next: (tags) => {
        this.choosableValueTagOptions = (tags || []).map((t) => ({ key: t.id, value: t.name }));
        this.loadingOptions = false;
      },
      error: () => (this.loadingOptions = false),
    });
  }

  // ── Output Emitter and Form Changes Subscription ────────────────────────

  private subscribeToFormChanges(): void {
    if (this.condition) {
      this.form
        .get('comparison')!
        .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((comparison: SessionConditionComparison) => this.onComparisonChanged(comparison));
    }
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.filter) {
        this.filterChange.emit(this.projectFilter());
      } else if (this.condition) {
        this.conditionChange.emit(this.projectCondition());
      }
    });
  }

  private projectFilter(): AudienceFilter {
    const raw = this.form.getRawValue();
    const updated: AudienceFilter = {
      ...this.filter,
      operator: raw.operator,
    };

    if (this.filterInputType === AudienceFilterInputType.MultiChoiceDropdown) {
      updated.value = (raw.valueArray || []).join(',');
    } else {
      updated.value = raw.value;
    }

    if (this.isAppTags && raw.filterValueTagString) {
      updated.value = raw.filterValueTagString;
    }

    if (updated.type === AudienceFilterType.Goals || updated.type === AudienceFilterType.Purchase) {
      updated.frequencyType = raw.frequencyType;
      updated.frequencyValue = raw.frequencyValue;

      // Timeframe encoding
      if (raw.timeOperator === AudienceFilterTimeOperator.AnyTime) {
        updated.timeOperator = undefined;
        updated.timeValue = undefined;
      } else {
        updated.timeOperator = raw.timeOperator;
        const timeVal: AudienceFilterTimeValue = {};
        if (raw.timeOperator === AudienceFilterTimeOperator.TimeWindow) {
          timeVal.days = raw.days;
        } else if (raw.timeOperator === AudienceFilterTimeOperator.SinceDate) {
          timeVal.startDate = raw.startDate ? raw.startDate.toISOString() : undefined;
        } else if (raw.timeOperator === AudienceFilterTimeOperator.BetweenDates) {
          timeVal.startDate = raw.startDate ? raw.startDate.toISOString() : undefined;
          timeVal.endDate = raw.endDate ? inclusiveEndDate(raw.endDate, this.storedEndDate) : undefined;
        } else if (raw.timeOperator === AudienceFilterTimeOperator.BetweenDaysAgo) {
          timeVal.days = raw.days;
          timeVal.upperDays = raw.daysUpper;
        }
        updated.timeValue = JSON.stringify(timeVal);
      }
    }

    if (updated.type === AudienceFilterType.Purchase) {
      if (raw.salesOperator === AudienceFilterSalesOperator.Any) {
        updated.purchaseProperties = null;
      } else {
        updated.purchaseProperties = {
          operator: raw.salesOperator,
          value: raw.salesValue,
          upperValue: raw.salesUpperValue,
          appliedCoupon: raw.appliedCoupon,
        };
      }
    }

    // Weather details
    if (updated.type === AudienceFilterType.Weather || updated.type === AudienceFilterType.Temperature) {
      const type =
        raw.weatherType === WeatherTypeModel.Temperature ? AudienceFilterType.Temperature : AudienceFilterType.Weather;
      updated.type = type;

      const wp: WeatherPropertiesModel = {
        weatherType: raw.weatherType,
        preferedApp: '',
        checkToday: raw.weatherTimeframe === TimeFrame.CheckToday,
        checkTomorrow: raw.weatherTimeframe === TimeFrame.CheckTomorrow,
        checkNextWeekend: raw.weatherTimeframe === TimeFrame.CheckNextWeekend,
        daysForward: raw.weatherTimeframe === TimeFrame.DaysForward ? raw.weatherDaysForward : null,
        inDays: raw.weatherTimeframe === TimeFrame.InDays ? raw.weatherInDays : null,
        specificDay: raw.weatherTimeframe === TimeFrame.SpecificDay ? raw.weatherSpecificDay?.toISOString() : null,
        customLongitude: raw.longitude,
        customLatitude: raw.latitude,
        minTemperature: raw.minTemp,
        maxTemperature: raw.maxTemp,
        minWindspeed: raw.minWind,
        maxWindspeed: raw.maxWind,
      };
      updated.weatherProperties = wp;

      if (type === AudienceFilterType.Weather) {
        updated.value = raw.weatherType;
      } else {
        updated.value = `Min ${raw.minTemp ?? 0}°C max ${raw.maxTemp ?? 0}°C`;
      }
    }

    return updated;
  }

  private projectCondition(): SessionCondition {
    const raw = this.form.getRawValue();
    const updated: SessionCondition = {
      ...this.condition,
    };

    if (this.isCampaignCondition) {
      const campaignEventBody = {
        campaignId: +raw.value,
        times: +raw.campaignEventCount,
      } as SessionConditionCampaignEventConfig;
      updated.value = JSON.stringify(campaignEventBody);
      updated.operator = raw.campaignEventOperator;
    } else {
      if (this.filterInputType === AudienceFilterInputType.MultiChoiceDropdown) {
        updated.value = (raw.valueArray || []).join(',');
      } else {
        updated.value = raw.value;
      }
      if (raw.comparison) {
        return applyComparison(updated, raw.comparison);
      }
      updated.operator = raw.operator;
    }

    return updated;
  }

  // ── UI Getters / Helpers ──────────────────────────────────────────────────

  protected get headerLabel(): string {
    if (this.filter) {
      const customName = this.getCustomIntegrationOverride()?.name;
      if (
        customName &&
        this.filter.app === AudienceFilterApp.Custom &&
        this.filter.type === AudienceFilterType.AppSegment
      ) {
        return `${customName} ${this.translateService.instant('audience.type-titles.apps.custom.segment-suffix')}`;
      }
      return this.translateService.instant(this.filter.filterConfig?.titleKey ?? '');
    }
    if (this.condition) {
      return this.translateService.instant(this.condition.conditionConfig?.titleKey ?? '');
    }
    return '';
  }

  protected get badgeIcon(): string {
    return this.filter?.filterConfig?.icon || this.condition?.conditionConfig?.icon || 'tune';
  }

  protected get badgeIconUrl(): string | undefined {
    if (this.filter?.app === AudienceFilterApp.Custom) {
      const customIconUrl = this.getCustomIntegrationOverride()?.iconUrl;
      if (customIconUrl) {
        return customIconUrl;
      }
    }
    return this.filter?.filterConfig?.iconUrl;
  }

  private getCustomIntegrationOverride(): { name?: string; iconUrl?: string } | undefined {
    return this.integrationsService.activeIntegrationsBS.getValue()?.find((i) => i.type === IntegrationType.Custom)
      ?.data;
  }

  protected get placeholderText(): string {
    if (this.condition) {
      const comparison: SessionConditionComparison | undefined = this.form.get('comparison')?.value;
      const condition = comparison
        ? applyComparison(this.condition, comparison)
        : { ...this.condition, operator: this.form.get('operator')?.value };
      return (
        SessionConditionHelpers.getPlaceholderKeyForConditionAndOperator(
          this.condition.conditionConfig,
          this.condition.type!,
          SessionConditionHelpers.getValueInputOperator(condition),
          this.accountDomain!,
          this.translateService,
        ) ?? ''
      );
    }
    return '';
  }

  protected getOperatorLabel(op: AudienceFilterOperator | string): string {
    const app = this.filter?.filterConfig?.app;
    const type = this.filter?.filterConfig?.type;
    // List / segment membership: "included" / "not included" is clearer than "equals" / "not equals".
    if (type === AudienceFilterType.AppList || type === AudienceFilterType.AppSegment) {
      if (op === AudienceFilterOperator.Equals) return `audience.operators.${AudienceFilterOperator.PartOf}`;
      if (op === AudienceFilterOperator.NotEquals) return `audience.operators.${AudienceFilterOperator.NotPartOf}`;
    }
    if (app === AudienceFilterApp.Custobar && type === AudienceFilterType.AppTags) {
      if (op === AudienceFilterOperator.Equals) return `audience.operators.${AudienceFilterOperator.HaveTag}`;
      if (op === AudienceFilterOperator.NotEquals) return `audience.operators.${AudienceFilterOperator.NotHaveTag}`;
    }
    return `audience.operators.${op}`;
  }

  protected onRemove(): void {
    this.remove.emit();
  }

  /** When the parent supplies the shared picker, ask it to target this row for
   *  replacement before the menu opens (the click also triggers the menu). */
  protected onTypeBtnClick(): void {
    if (this.sharedTypeMenu) this.changeTypeRequested.emit();
  }

  protected get isAppTags(): boolean {
    return this.filter?.type === AudienceFilterType.AppTags;
  }
}
